<template>
	<VerticalLayout>
		{{ $t('dateRangeChooser.' + (showTime ? 'dateTimesRangeLabel' : 'datesRangeLabel')) }}
		<RangePicker
			v-bind="rangePickerValueBind"
			:showTime="showTime"
			:format="dateFormat"
			:disabledDate="disabledDate"
			:getPopupContainer="getPopupContainer"
			@openChange="onOpenChange"
			@calendarChange="onCalendarChange"
			@change="onDatesRangeChange"
		/>
	</VerticalLayout>
</template>

<script lang="ts">
import { ref, toRef, computed, watch } from 'vue';
import { RangePicker } from 'ant-design-vue';

import { DATE_FORMAT, DATETIME_FORMAT } from '@/components/common/dateUtils';
import {
	clampDayjsRangeToMaxSpan,
	createMaxSpanDisabledDate,
	isCompleteDayjsRange,
	isSameDayjsRange,
} from '@/utils/dateRangeMaxSpanUtils';
import type {
	CalendarChangeRange,
	DateRangeDayjs,
	DayjsRange,
	DayjsRangeEnd,
	DayjsRangeNullable,
} from '@/utils/dateRangeMaxSpanUtils';

import { usePopupContainer } from '@/utils/usePopupContainer';

import VerticalLayout from '@/components/common/VerticalLayout.vue';


// Полный валидный диапазон для RangePicker либо отсутствие выбранного диапазона.
type RangePickerValue = [DateRangeDayjs, DateRangeDayjs] | null;
// Значение из Ant Design: даты, строковое представление дат или null.
type RangePickerChangeValue = [DateRangeDayjs, DateRangeDayjs] | [string, string] | null;
// Техническое значение для управления отображением календаря во время выбора.
type RangePickerHackValue = RangePickerValue | [];
// Значение, передаваемое RangePicker для отображения; undefined сбрасывает его.
type AntdRangePickerDisplayValue = [DateRangeDayjs, DateRangeDayjs] | [string, string] | undefined;


const parseRangePickerValue = (value: RangePickerChangeValue): DayjsRangeNullable => {
	if (value == null) {
		return null;
	}
	if (typeof value[0] === 'string') {
		return null;
	}
	return value as DayjsRange;
};


const toRangePickerValue = (value: DayjsRangeNullable): RangePickerValue => {
	if (!isCompleteDayjsRange(value)) {
		return null;
	}
	return value;
};

export default {
	components: {
		RangePicker,
		VerticalLayout,
	},
	emits: ['update:modelValue', 'change'],
	props: {
		modelValue: {
			type: Array,
			default() {
				return [];
			},
		},
		showTime: {
			type: Boolean,
			default: false,
		},
		maxSpanDays: {
			type: Number,
			default: 5,
		},
	},
	setup(props, { emit }) {
		const modelValue = toRef(props, 'modelValue');
		const showTimeRef = toRef(props, 'showTime');
		const maxSpanDaysRef = toRef(props, 'maxSpanDays');
		const calendarRange = ref<CalendarChangeRange>(null);
		const hackValue = ref<RangePickerHackValue | undefined>(undefined);
		const lastChangedEnd = ref<DayjsRangeEnd | null>(null);

		const datesRangeValue = computed<RangePickerValue>({
			get: () => {
				if (modelValue.value == null || modelValue.value.length !== 2) {
					return null;
				}
				const range = modelValue.value as DayjsRangeNullable;
				return toRangePickerValue(range);
			},
			set: value => {
				emit('update:modelValue', value);
				emit('change', value);
			},
		});

		const displayValue = computed((): AntdRangePickerDisplayValue => {
			if (hackValue.value !== undefined) {
				if (hackValue.value == null) {
					return undefined;
				}
				return hackValue.value as AntdRangePickerDisplayValue;
			}
			return datesRangeValue.value ?? undefined;
		});

		const rangePickerValueBind = computed(() => {
			const currentValue = displayValue.value;
			if (currentValue === undefined) {
				return {};
			}
			return { value: currentValue };
		});

		const dateFormat = computed(() => {
			return showTimeRef.value ? DATETIME_FORMAT : DATE_FORMAT;
		});

		const disabledDate = computed(() => {
			return createMaxSpanDisabledDate(
				maxSpanDaysRef.value,
				() => calendarRange.value,
			);
		});

		const emitClampedRange = (value: DayjsRangeNullable) => {
			const clamped = clampDayjsRangeToMaxSpan(
				value,
				maxSpanDaysRef.value,
				lastChangedEnd.value,
			);
			lastChangedEnd.value = null;
			datesRangeValue.value = toRangePickerValue(clamped);
		};

		watch(
			datesRangeValue,
			value => {
				const clamped = clampDayjsRangeToMaxSpan(value, maxSpanDaysRef.value);
				if (!isSameDayjsRange(value, clamped)) {
					datesRangeValue.value = toRangePickerValue(clamped);
				}
			},
			{ immediate: true },
		);

		const onOpenChange = (open: boolean) => {
			if (open) {
				calendarRange.value = null;
				hackValue.value = [];
			} else {
				hackValue.value = undefined;
			}
		};

		const onCalendarChange = (
			values: RangePickerChangeValue,
			_formatString: [string, string],
			info: { range: DayjsRangeEnd },
		) => {
			calendarRange.value = parseRangePickerValue(values);
			lastChangedEnd.value = info?.range ?? null;
		};

		const onDatesRangeChange = (value: RangePickerChangeValue) => {
			hackValue.value = undefined;
			emitClampedRange(parseRangePickerValue(value));
		};

		const { getPopupContainer } = usePopupContainer();

		return {
			rangePickerValueBind,
			dateFormat,
			disabledDate,
			onOpenChange,
			onCalendarChange,
			onDatesRangeChange,
			getPopupContainer,
		};
	},
};
</script>
